import type { MarkLineComponentOption } from 'echarts';
import {
  BarChartData,
  calculateThresholdStepValue,
  formatValue,
  getOverriddenProperties,
  ModeOption,
  SeriesOverrides,
  ThresholdColorPalette,
  ThresholdOptions,
  UnitOptions,
} from '@/core';
import type { ChartApi } from '../EChart';
import type { CursorCoordinates } from '../PanelTooltip/model';
import { DEFAULT_NO_DATA_MESSAGE } from '../utils';
import { BarChartTooltipData } from './model';

/**
 * Helper to return the correct bar color considering overrides and thresholds.
 */
export const getBarColor = ({
  itemStyleParams,
  overrides,
  primaryColor,
  thresholds,
  thresholdColors,
  maxValue,
}: {
  itemStyleParams: { name: string; color: string; value: number };
  overrides?: SeriesOverrides;
  primaryColor?: string;
  thresholds?: ThresholdOptions;
  thresholdColors: ThresholdColorPalette;
  maxValue?: number;
}) => {
  // Determine if thresholds affect the color
  if (thresholds?.steps?.length) {
    const thresholdColor = determineColorByThreshold({
      value: itemStyleParams.value,
      thresholds,
      thresholdColors,
      maxValue,
    });
    if (thresholdColor) {
      return thresholdColor;
    }
  }

  // Color overrides resolve after thresholds
  const overriddenProperties = getOverriddenProperties(itemStyleParams.name, overrides);
  if (overriddenProperties.color) {
    return overriddenProperties.color;
  }

  return primaryColor ?? itemStyleParams.color;
};
/**
 * When threshold steps are defined, bar color is dynamic based on whether its value exceeds the threshold.
 */
export function determineColorByThreshold({
  value,
  thresholds,
  thresholdColors,
  maxValue,
}: {
  value: number;
  thresholds?: ThresholdOptions;
  thresholdColors: ThresholdColorPalette;
  maxValue?: number;
}): string | undefined {
  if (!thresholds?.steps || thresholds.steps.length === 0) return undefined;

  // Loop through thresholds backwards since they are in ascending order and we can early return when data value exceeds step value
  for (let i = thresholds.steps.length - 1; i >= 0; i--) {
    const step = thresholds.steps[i];
    if (!step) continue;
    const thresholdValue = calculateThresholdStepValue(step, thresholds.mode, maxValue);
    if (value >= thresholdValue) {
      const stepPaletteColor = thresholdColors.palette[i] ?? thresholdColors.defaultColor;
      return step.color ?? stepPaletteColor; // Fallback to charts theme color if step color not explicitly defined
    }
  }

  return undefined;
}

/**
 * ECharts markLine is used to plot a dashed line for each threshold step.
 * https://echarts.apache.org/en/option.html#series-bar.markLine
 */
export function getThresholdMarkLine({
  thresholds,
  thresholdColors,
  maxValue,
}: {
  thresholds?: ThresholdOptions;
  thresholdColors: ThresholdColorPalette;
  maxValue?: number;
}): MarkLineComponentOption {
  const thresholdsSteps = thresholds?.steps;
  if (!thresholdsSteps || !thresholdsSteps.length) return { data: [] };
  return {
    data: thresholdsSteps.map((step, index) => {
      const thresholdValue = calculateThresholdStepValue(step, thresholds.mode, maxValue);
      const stepPaletteColor = thresholdColors.palette[index] ?? thresholdColors.defaultColor;
      return {
        xAxis: thresholdValue,
        lineStyle: { color: step.color ?? stepPaletteColor },
        label: {
          show: true,
          position: 'end',
          formatter: step.name ?? '',
          color: thresholdColors.labelColor,
          backgroundColor: thresholdColors.labelBackgroundColor,
          padding: step.name ? [3, 4, 1, 4] : 0,
        },
      };
    }),
  };
}

/**
 * Get the bar chart tooltip data for the given data index.
 */
export function getTooltipData({
  data,
  dataIndex,
  overrides,
  primaryColor,
  thresholds,
  thresholdColors,
  maxValue,
}: {
  data: BarChartData[];
  dataIndex: number;
  overrides?: SeriesOverrides;
  primaryColor?: string;
  thresholds?: ThresholdOptions;
  thresholdColors: ThresholdColorPalette;
  maxValue?: number;
}): BarChartTooltipData | undefined {
  const datum = data?.[dataIndex];
  if (!datum) return;
  const getColor = (value: number | null | undefined): string => {
    if (value === undefined || value === null) return '';
    return getBarColor({
      itemStyleParams: {
        name: datum.label,
        value,
        // Unlikely to be used since primaryColor is set from Categorical palette in BarChartPanel.
        // In future, consider making primaryColor required on BarChartBase.
        color: '',
      },
      overrides,
      primaryColor,
      thresholds,
      thresholdColors,
      maxValue,
    });
  };
  return {
    label: datum.label,
    value: datum.value,
    comparisonValue: datum.comparisonValue,
    color: getColor(datum.value),
    comparisonColor: getColor(datum.comparisonValue),
  };
}

export type BandAtCoordsDeps = {
  getBandDataAtIndex: ((index: number) => BarChartTooltipData | undefined) | undefined;
};

/**
 * Compute the band data to show in tooltip at the given coordinates.
 */
export function computeBandAtCoords(
  convertFromPixel: ChartApi['convertFromPixel'],
  coords: CursorCoordinates | null,
  { getBandDataAtIndex }: BandAtCoordsDeps
): BarChartTooltipData | undefined {
  if (!convertFromPixel || !coords || !getBandDataAtIndex) return;
  if (coords.plotCanvas.x === undefined || coords.plotCanvas.y === undefined) return;
  const point: [number, number] = [coords.plotCanvas.x, coords.plotCanvas.y];
  const logicalCoords = convertFromPixel({ gridIndex: 0 }, point);
  if (!logicalCoords || logicalCoords[1] === undefined) return;
  const closestBarIndex = logicalCoords[1];
  return getBandDataAtIndex(closestBarIndex);
}

/**
 * Account for percentage mode when formatting value, used in tooltip content.
 */
export function formatDisplayValue({
  value,
  mode,
  unit,
}: {
  value: number | undefined;
  mode: ModeOption;
  unit: UnitOptions;
}) {
  if (value === undefined) return DEFAULT_NO_DATA_MESSAGE;
  if (mode === 'percentage') {
    return formatValue(value, {
      ...unit,
      kind: 'Percent',
    });
  }
  return formatValue(value, unit);
}
